import React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { Preview } from '@renderer/components'
import Button from '@renderer/components/Button'
import { useConfigStore } from '@renderer/stores/configState'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { useDesignLayout } from '@renderer/hooks'

const CameraPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState } = useSessionStateStore()
  const { selectedSecondaryCamera } = useSettingsStore()
  const { config } = useConfigStore()
  const { primaryColor } = config?.theme || { primaryColor: '#3B82F6' }
  const { shortSide } = useDesignLayout()

  const handleCameraSelect = (camera: 'primary' | 'secondary'): void => {
    setSessionState({
      userPreferences: {
        selectedCamera: camera
      },
      timeline: {
        activityLogs: [
          {
            page: 'camera',
            type: 'ui',
            action: 'camera_selected',
            details: { camera },
            timestamp: new Date()
          }
        ]
      }
    })
    
    // Determine next page based on startWithPayment and applyAfterCapture
    const welcomeSettings = config?.pages?.welcome?.settings
    const filterSettings = config?.pages?.filter?.settings
    const applyAfterCapture = filterSettings?.applyAfterCapture || false
    const startWithPayment = welcomeSettings?.startWithPayment || false
    
    let nextPage: string
    if (startWithPayment && applyAfterCapture) {
      // When startWithPayment is true and applyAfterCapture is true,
      // user preferences group is: camera → frame (filter is skipped)
      nextPage = 'frame'
    } else if (config?.pages?.filter?.settings?.enabled) {
      // Normal flow: go to filter if enabled
      nextPage = 'filter'
    } else {
      // Filter disabled: go to frame
      nextPage = 'frame'
    }
    
    stateUploader.uploadSessionState()
    navigate(`/${projectId}/${nextPage}`)
  }

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 p-8">
      <h1 className="text-4xl font-bold mb-8">{t('camera.title', 'Choose Your Camera')}</h1>
      <div
        className={`grid w-full max-w-6xl gap-8 ${shortSide >= 768 ? 'grid-cols-2' : 'grid-cols-1'}`}
      >
        {/* Primary Camera */}
        <div className="flex flex-col items-center">
          <div className="w-full aspect-video bg-black mb-4 rounded-lg overflow-hidden">
            <Preview cameraKey="primary" />
          </div>
          <Button
            text={t('camera.primary', 'Primary Camera')}
            onClick={() => handleCameraSelect('primary')}
            style={{ backgroundColor: primaryColor }}
            className="!text-white !font-bold !py-3 !px-8 !text-xl"
          />
        </div>

        {/* Secondary Camera */}
        {selectedSecondaryCamera && (
          <div className="flex flex-col items-center">
            <div className="w-full aspect-video bg-black mb-4 rounded-lg overflow-hidden">
              <Preview cameraKey="secondary" />
            </div>
            <Button
              text={t('camera.secondary', 'Secondary Camera')}
              onClick={() => handleCameraSelect('secondary')}
              style={{ backgroundColor: primaryColor }}
              className="!text-white !font-bold !py-3 !px-8 !text-xl"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default CameraPage
